'use client';

import { useState, useEffect, useRef } from 'react';
import { Button, ButtonLink } from '@packages/shared/src/ui/Button/Button';
import { useIntl } from 'react-intl';
import { useAuthQuery } from '@packages/shared/src/hooks/useAuthQuery/useAuthQuery';
import { CustomerInvoiceDocumentUrlDocument } from '@packages/gql/generated/shopping/CustomerInvoiceDocumentUrlDocument';
import { CircularProgress } from '@packages/shared/src/ui/CircularProgress/CircularProgress';
import { messages } from './messages';

/* GraphQL */ `
  query CustomerInvoiceDocumentUrl($customerInvoiceDocumentUrlId: String!, $locale: String!) {
    customerInvoiceDocumentUrl(id: $customerInvoiceDocumentUrlId, locale: $locale) {
      url
    }
  }
`;

interface InvoiceDownloadButtonProps {
  invoiceNumber: string;
}

export const InvoiceDownloadButton: React.FC<InvoiceDownloadButtonProps> = ({ invoiceNumber }) => {
  const [requestState, setRequestState] = useState<'ready' | 'fetching' | 'cooldown'>('ready');
  const lastProcessedUrl = useRef<string | null>(null);
  const { formatMessage, locale } = useIntl();

  const [{ data, error }, executeQuery] = useAuthQuery({
    query: CustomerInvoiceDocumentUrlDocument,
    variables: {
      customerInvoiceDocumentUrlId: invoiceNumber,
      locale,
    },
    requestPolicy: 'network-only',
    pause: true,
  });

  // opens URL when received
  useEffect(() => {
    const docUrl = data?.customerInvoiceDocumentUrl?.url;
    if (docUrl && lastProcessedUrl.current !== docUrl && requestState !== 'cooldown') {
      lastProcessedUrl.current = docUrl;

      // we should be able to trust the url not sure this sanitization is necessary
      const fullUrl =
        docUrl.startsWith('http://') || docUrl.startsWith('https://')
          ? docUrl
          : new URL(docUrl, window.location.origin).toString();

      // open new tab noopener noreferrer for security
      window.open(fullUrl, '_blank', 'noopener,noreferrer');

      // start 10s cooldown
      setRequestState('cooldown');
    }
  }, [data, requestState]);

  // sets 10s cooldown
  useEffect(() => {
    let timeout: NodeJS.Timeout;

    if (requestState === 'cooldown') {
      timeout = setTimeout(() => setRequestState('ready'), 10000);
    }

    return () => clearTimeout(timeout);
  }, [requestState]);

  const handleDownload = () => {
    if (requestState === 'cooldown') return;

    setRequestState('fetching');
    executeQuery();
  };

  const showLinkAvailable = requestState === 'cooldown';
  const showDownloadButton = requestState !== 'cooldown';

  const ButtonComponent = showLinkAvailable ? ButtonLink : Button;

  return (
    <ButtonComponent
      className="m-auto mb-1.5 w-64"
      target={showLinkAvailable ? '_blank' : undefined}
      href={showLinkAvailable ? (lastProcessedUrl.current ?? undefined) : undefined}
      onClick={showDownloadButton ? handleDownload : undefined}
      disabled={!!error}
    >
      {showDownloadButton && formatMessage(messages.downloadButton)}
      {showLinkAvailable && formatMessage(messages.showAvailableLink)}
      {requestState === 'fetching' ? <CircularProgress /> : <span className="icon-emp-download" />}
    </ButtonComponent>
  );
};
